/* TourLibrary — categorised hub of in-app tours for the doctor portal.
 *
 * iter98.92 — Layout v2: single dense responsive grid (4 cols on lg,
 * 2 on md, 1 on sm) instead of the 7-section vertical stack that
 * required a "long long scroll" to reach the bottom-most categories.
 * Categories are still surfaced — but as small chips IN the cards
 * rather than as section headers ABOVE rows. The result is the
 * entire library fits in a single fold on a 1080p+ screen.
 *
 * Hero is compact: one-row layout with the progress ribbon on the
 * right. The "Replay portal overview" CTA in the hero kicks off the
 * legacy 90-sec walkthrough (formerly accessed via the "Take the
 * product tour" sidebar button — that button was retired to remove
 * the two-doorway confusion).
 *
 * iter100.175 — The Demo Patient sandbox strip + "Reset demo" and the
 * "Refer a colleague" panel were removed from this page. Referral now
 * lives on the doctor's Profile page (semantically correct — it's a
 * personal growth action) with a Quick-Action shortcut on the
 * Dashboard for daily discoverability.
 */
import React, { useEffect, useState, useMemo } from "react";
import { useTour } from "@/components/tour/TourProvider";
import { TOURS, TOUR_CATEGORIES, DOCTOR_TOUR_IDS } from "@/components/tour/tours";
import api from "@/lib/api";
import {
    Compass, Cube, CheckCircle, ArrowsClockwise, Users, Calendar,
    ChatsCircle, Receipt, UserCircle, PlayCircle, Lock, Sparkle,
} from "@phosphor-icons/react";

const ICON_LOOKUP = {
    Compass, Cube, CheckCircle, ArrowsClockwise, Users, Calendar,
    ChatsCircle, Receipt, UserCircle,
};

/* Card accent palettes per category. Used for the icon backdrop and
 * the category chip. Subtle enough to stay calm in a grid of 8. */
const CATEGORY_ACCENTS = {
    violet:  { tile: "bg-violet-50  text-violet-700",  chip: "bg-violet-100  text-violet-800  ring-violet-200" },
    teal:    { tile: "bg-teal-50    text-teal-700",    chip: "bg-teal-100    text-teal-800    ring-teal-200" },
    blue:    { tile: "bg-blue-50    text-blue-700",    chip: "bg-blue-100    text-blue-800    ring-blue-200" },
    amber:   { tile: "bg-amber-50   text-amber-700",   chip: "bg-amber-100   text-amber-800   ring-amber-200" },
    cyan:    { tile: "bg-cyan-50    text-cyan-700",    chip: "bg-cyan-100    text-cyan-800    ring-cyan-200" },
    emerald: { tile: "bg-emerald-50 text-emerald-700", chip: "bg-emerald-100 text-emerald-800 ring-emerald-200" },
    rose:    { tile: "bg-rose-50    text-rose-700",    chip: "bg-rose-100    text-rose-800    ring-rose-200" },
};

export default function TourLibrary() {
    const { start } = useTour();
    const [progress, setProgress] = useState({});
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let alive = true;
        api.get("/doctor/tour-progress")
            .then((r) => {
                if (!alive) return;
                const map = {};
                (r.data || []).forEach((p) => { map[p.tour_id] = p; });
                setProgress(map);
            })
            .catch(() => { /* graceful — fresh state */ })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, []);

    /* iter100.175 — Removed `resetDemo` handler; the Demo Patient
       sandbox strip is gone from this page. Refer-a-colleague also
       moved to the Profile page. */

    /* Flatten the catalogue — keep category info on each tour so the
       cards can render their own category chip + accent colour. Sort
       deterministically: available tours first, then by category
       order, then by tour order within the category. */
    const allTours = useMemo(() => {
        const out = DOCTOR_TOUR_IDS
            .map((id) => {
                const t = TOURS[id];
                if (!t) return null;
                return { id, ...t };
            })
            .filter(Boolean);
        return out.sort((a, b) => {
            const aCat = Object.keys(TOUR_CATEGORIES).indexOf(a.category);
            const bCat = Object.keys(TOUR_CATEGORIES).indexOf(b.category);
            if (aCat !== bCat) return aCat - bCat;
            return (a.order || 99) - (b.order || 99);
        });
    }, []);

    const availableTotal = allTours.filter((t) => t.available).length;
    const completedCount = Object.keys(progress).filter((id) => DOCTOR_TOUR_IDS.includes(id)).length;
    const progressPct = availableTotal > 0 ? Math.round((completedCount / availableTotal) * 100) : 0;

    return (
        <div className="space-y-4" data-testid="tour-library">
            {/* HERO — compact single-row layout */}
            <div className="rounded-3xl border border-border bg-gradient-to-br from-primary/[0.05] via-card to-card px-5 py-4 sm:px-6 sm:py-5">
                <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div className="min-w-0 flex-1">
                        <div className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.22em] font-bold uppercase text-primary">
                            <Sparkle size={11} weight="fill" /> Help &amp; tours
                        </div>
                        <h1 className="font-display text-2xl sm:text-3xl font-bold mt-1.5 tracking-tight leading-tight">
                            Learn the portal, one workflow at a time
                        </h1>
                        <p className="text-[13px] text-muted-foreground mt-1 leading-relaxed max-w-2xl">
                            Short, focused tours — each under 5 minutes. Tours run inside the real portal pages with overlays highlighting the exact buttons to click.
                        </p>
                    </div>

                    {/* Progress ribbon — minimised */}
                    <div
                        className="rounded-xl border border-emerald-200 bg-emerald-50/60 px-3.5 py-2.5 shrink-0"
                        data-testid="tour-progress-ribbon"
                    >
                        <div className="text-[9px] font-bold tracking-[0.18em] uppercase text-emerald-700">
                            Your progress
                        </div>
                        <div className="flex items-baseline gap-1.5 mt-0.5">
                            <div className="text-xl font-display font-bold text-emerald-900">
                                {loading ? "…" : completedCount}
                            </div>
                            <div className="text-xs text-emerald-700/70 font-medium">of {availableTotal}</div>
                        </div>
                        <div className="mt-1.5 h-1 w-32 rounded-full bg-emerald-100 overflow-hidden">
                            <div className="h-full bg-emerald-500 transition-all" style={{ width: `${progressPct}%` }} />
                        </div>
                    </div>
                </div>

                {/* Replay portal overview — replaces the old sidebar "Take the
                    product tour" pill. Surfaces it here where doctors expect
                    to find tours, not in a corner of the sidebar. */}
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                    <button
                        type="button"
                        onClick={() => start("doctor")}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold rounded-full text-primary bg-primary/8 hover:bg-primary/15 ring-1 ring-primary/15 hover:ring-primary/30 transition-all"
                        data-testid="library-replay-overview-btn"
                    >
                        <PlayCircle size={12} weight="fill" />
                        Replay the 90-sec portal overview
                    </button>
                </div>
            </div>

            {/* iter100.175 — Demo Patient sandbox strip removed. Doctors
                found it noisy on a page they visited to LEARN, and it
                inflated their patient list with a "Riya Sharma" ghost
                row. The reset endpoint stays on the backend as a
                deprecated no-op for any old bookmarks. */}

            {/* DENSE GRID — 4 cols on lg, 2 on md, 1 on sm */}
            <div
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3"
                data-testid="tour-cards-grid"
            >
                {allTours.map((tour) => (
                    <TourCard
                        key={tour.id}
                        tour={tour}
                        completed={!!progress[tour.id]}
                        onStart={() => start(tour.id)}
                    />
                ))}
            </div>

            {/* iter100.175 — "Refer a colleague" panel moved to
                /doctor/profile#refer where doctors naturally think about
                growth actions. Discoverability preserved via the
                Dashboard's Quick Actions "Refer" tile. */}
        </div>
    );
}


function TourCard({ tour, completed, onStart }) {
    const Icon = ICON_LOOKUP[tour.icon] || Compass;
    const disabled = !tour.available;
    const catMeta = TOUR_CATEGORIES[tour.category] || { label: tour.category, color: "violet" };
    const accent = CATEGORY_ACCENTS[catMeta.color] || CATEGORY_ACCENTS.violet;
    return (
        <button
            type="button"
            onClick={disabled ? undefined : onStart}
            disabled={disabled}
            className={`group relative text-left rounded-2xl border bg-card p-3.5 transition-all w-full h-full flex flex-col ${
                disabled
                    ? "border-border opacity-65 cursor-not-allowed"
                    : "border-border hover:border-primary/40 hover:shadow-md hover:-translate-y-0.5"
            }`}
            data-testid={`tour-card-${tour.id}`}
        >
            {/* Top row — icon + status */}
            <div className="flex items-start justify-between gap-2">
                <div className={`w-9 h-9 rounded-xl grid place-items-center shrink-0 ${
                    disabled
                        ? "bg-slate-100 text-slate-400"
                        : completed
                            ? "bg-emerald-50 text-emerald-600"
                            : accent.tile
                }`}>
                    <Icon size={18} weight="duotone" />
                </div>
                {completed && (
                    <span
                        className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-[0.14em] text-emerald-700 bg-emerald-50 ring-1 ring-emerald-200 px-1.5 py-0.5 rounded-full"
                        data-testid={`tour-card-completed-${tour.id}`}
                    >
                        <CheckCircle size={9} weight="fill" /> Done
                    </span>
                )}
                {disabled && (
                    <span className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-[0.14em] text-slate-600 bg-slate-100 ring-1 ring-slate-200 px-1.5 py-0.5 rounded-full">
                        <Lock size={9} weight="fill" /> Soon
                    </span>
                )}
            </div>

            {/* Title + description */}
            <div className="mt-2.5 min-w-0 flex-1">
                <h3 className="font-display text-[13px] font-bold text-foreground leading-snug">{tour.title}</h3>
                <p className="text-[11.5px] text-muted-foreground mt-1 leading-relaxed line-clamp-2">{tour.description}</p>
            </div>

            {/* Footer — category chip + duration + Start indicator */}
            <div className="mt-3 flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5 min-w-0">
                    <span className={`text-[9px] font-bold uppercase tracking-[0.14em] px-1.5 py-0.5 rounded-full ring-1 ${accent.chip}`}>
                        {catMeta.label}
                    </span>
                    <span className="text-[10px] text-muted-foreground/80 font-mono">{tour.duration}</span>
                </div>
                {!disabled && (
                    <span className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-primary group-hover:gap-1 transition-all">
                        <PlayCircle size={11} weight="fill" />
                        {completed ? "Replay" : "Start"}
                    </span>
                )}
            </div>
        </button>
    );
}
